import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  const lastUpdated = "March 20, 2026";

  return (
    <div className="min-h-screen bg-paper">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-ink-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <CalendarCheck className="w-7 h-7 text-ink" />
              <span className="text-xl font-bold text-ink font-display">Enota</span>
            </Link>
            <Link to="/" className="text-ink-600 hover:text-ink flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-md border border-ink-200 shadow-sm p-8 md:p-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-ink font-display mb-2">
              Terms of Use
            </h1>
            <p className="text-ink-600 mb-8">Last Updated: {lastUpdated}</p>

            <div className="prose prose-slate max-w-none">
              <p className="text-ink-600 leading-relaxed mb-6">
                Welcome to Enota, a service owned and operated by <strong>OFFER GADGET LLC</strong>. 
                These Terms of Use ("Terms") govern your access to and use of Enota's website, 
                applications, and services (collectively, the "Service"). Please read these Terms carefully 
                before using our Service.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                By accessing or using the Service, you agree to be bound by these Terms and our Privacy 
                Policy. If you do not agree to these Terms, you may not access or use the Service. We 
                reserve the right to modify these Terms at any time, and such modifications shall be 
                effective immediately upon posting on the Service.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                Your continued use of the Service after any modifications indicates your acceptance of 
                the modified Terms. We encourage you to review these Terms periodically for any changes.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                2. Eligibility
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                You must be at least 18 years of age to use the Service. By using the Service, you 
                represent and warrant that you are at least 18 years old and have the legal capacity 
                to enter into these Terms. If you are using the Service on behalf of an organization, 
                you represent and warrant that you have the authority to bind that organization to 
                these Terms.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                3. Account Registration and Security
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                To access certain features of the Service, you must register for an account. When 
                registering, you agree to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security and confidentiality of your login credentials</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Immediately notify us of any unauthorized use of your account</li>
              </ul>
              <p className="text-ink-600 leading-relaxed mb-4">
                We reserve the right to suspend or terminate your account if any information provided 
                proves to be inaccurate, outdated, or incomplete, or if we suspect fraudulent or 
                unauthorized activity.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                4. Subscription Plans and Payments
              </h2>
              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">4.1 Free and Paid Plans</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                Enota offers both free and paid subscription plans. Free accounts are limited 
                in features and capacity as described on our pricing page. Paid subscriptions provide 
                additional features and increased capacity.
              </p>
              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">4.2 Billing</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                For paid subscriptions, you agree to pay all applicable fees as described at the time 
                of purchase. Fees are charged in advance on a monthly or annual basis, depending on 
                your selected billing cycle. All payments are processed through our third-party payment 
                processor, Stripe.
              </p>
              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">4.3 Automatic Renewal</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                Paid subscriptions automatically renew at the end of each billing period unless you 
                cancel before the renewal date. You authorize us to charge your payment method on file 
                for the renewal fee.
              </p>
              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">4.4 Refunds</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                Payments are non-refundable except as required by law or as explicitly stated in these 
                Terms. If you believe you have been charged in error, please contact our support team 
                within 30 days of the charge.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                5. Acceptable Use Policy
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                You agree not to use the Service to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Violate any applicable laws, regulations, or third-party rights</li>
                <li>Upload, transmit, or distribute any malicious code, viruses, or harmful content</li>
                <li>Attempt to gain unauthorized access to the Service or its related systems</li>
                <li>Interfere with or disrupt the integrity or performance of the Service</li>
                <li>Engage in any activity that could harm, disable, or impair the Service</li>
                <li>Use automated means (bots, scrapers, etc.) to access the Service without permission</li>
                <li>Impersonate any person or entity or misrepresent your affiliation</li>
                <li>Collect or harvest any information from other users without consent</li>
                <li>Use the Service for any illegal, fraudulent, or unauthorized purpose</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                6. Intellectual Property Rights
              </h2>
              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">6.1 Our Intellectual Property</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                The Service and its original content, features, and functionality are owned by 
                Enota and are protected by international copyright, trademark, patent, trade 
                secret, and other intellectual property laws. Our trademarks, service marks, and 
                logos may not be used without our prior written consent.
              </p>
              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">6.2 Your Content</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                You retain ownership of any content you submit to the Service ("User Content"). By 
                submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license 
                to use, reproduce, modify, and display such content solely for the purpose of providing 
                the Service to you.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                7. Third-Party Services
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                The Service may integrate with or contain links to third-party services, including 
                payment processors, email providers, and SMS services. Your use of such third-party 
                services is subject to their respective terms and privacy policies. We are not 
                responsible for the content, privacy practices, or availability of third-party services.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                8. Disclaimer of Warranties
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4 uppercase text-sm">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
                EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND TITLE.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                We do not warrant that the Service will be uninterrupted, error-free, secure, or free 
                of viruses or other harmful components. We do not guarantee that renewal reminders will 
                be delivered on time or at all, and you acknowledge that you are solely responsible for 
                managing your own renewal deadlines.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                9. Limitation of Liability
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4 uppercase text-sm">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, ENOTA AND ITS OFFICERS, DIRECTORS, 
                EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
                CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, 
                DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                In no event shall our total liability exceed the amount you paid to us, if any, during 
                the twelve (12) months preceding the claim. Some jurisdictions do not allow the exclusion 
                or limitation of certain damages, so some of the above limitations may not apply to you.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                10. Indemnification
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                You agree to indemnify, defend, and hold harmless Enota and its officers, directors, 
                employees, agents, and affiliates from and against any claims, liabilities, damages, losses, 
                and expenses, including reasonable attorneys' fees, arising out of or in any way connected 
                with your access to or use of the Service, your violation of these Terms, or your violation 
                of any third-party rights.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                11. Termination
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We may terminate or suspend your access to the Service immediately, without prior notice 
                or liability, for any reason, including but not limited to a breach of these Terms. Upon 
                termination, your right to use the Service will immediately cease.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                You may terminate your account at any time by contacting our support team or through your 
                account settings. Upon termination, we may delete your data in accordance with our data 
                retention policies.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                12. Governing Law and Dispute Resolution
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the State 
                of Delaware, United States, without regard to its conflict of law provisions. Any disputes 
                arising from these Terms or the Service shall be resolved through binding arbitration in 
                accordance with the American Arbitration Association's rules.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                13. Severability
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                If any provision of these Terms is found to be unenforceable or invalid, that provision 
                shall be limited or eliminated to the minimum extent necessary so that these Terms shall 
                otherwise remain in full force and effect.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                14. Entire Agreement
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                These Terms, together with our Privacy Policy, constitute the entire agreement between 
                you and Enota regarding the Service and supersede all prior agreements, 
                understandings, and communications, whether written or oral.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                15. Contact Information
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="bg-paper p-4 rounded-md border border-ink-200 mb-4">
                <p className="text-ink font-bold">OFFER GADGET LLC</p>
                <p className="text-ink-600">53 Calle Palmeras Ste 601</p>
                <p className="text-ink-600">San Juan, PR 00901</p>
                <p className="text-ink-600 mt-2">Email: support@renewalnote.com</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-ink text-ink-600/70 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-6 h-6 text-white" />
              <span className="text-lg font-bold text-white font-display">Enota</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/california-privacy" className="hover:text-white transition-colors">California Residents</Link>
              <Link to="/login" className="hover:text-white transition-colors">Login</Link>
            </div>
            <p className="text-sm">&copy; 2026 Enota. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
